import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorMessage } from "../lib/api";
import { toast } from "sonner";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { ArrowLeft, Save, Send, CheckCircle2, Plus, X, Lock } from "lucide-react";

const TECH_OPTIONS = ["React", "Python", "FastAPI", "Node.js", "Flutter", "MongoDB", "PostgreSQL", "AWS", "GCP", "Firebase", "TensorFlow", "Next.js", "Vue", "Django", "Rust", "Go"];

const PROTOTYPE_STATUS = [
  "Fully working demo",
  "Partially working",
  "UI mockup only",
  "Hardware prototype",
  "Concept with code snippets",
];

// value = canonical stored label (display label shown to user)
const PRIZE_USAGE = [
  { value: "Cloud hosting & infrastructure", label: "Cloud hosting & infrastructure (AWS, GCP, Azure)" },
  { value: "AI/ML credits & APIs", label: "AI/ML credits & APIs (OpenAI, Gemini, Hugging Face)" },
  { value: "Software tools & subscriptions", label: "Software tools & subscriptions (GitHub, Figma, Notion etc.)" },
  { value: "Hiring a developer or designer", label: "Hiring a developer or designer" },
  { value: "Marketing & user acquisition", label: "Marketing & user acquisition" },
  { value: "Legal & company registration", label: "Legal & company registration" },
  { value: "Hardware & physical components", label: "Hardware & physical components" },
  { value: "Learning & upskilling", label: "Learning & upskilling (courses, certifications)" },
  { value: "General business operations", label: "General business operations" },
  { value: "Other", label: "Other" },
];

// Role options kept broad so non-IT / hardware / business ideas fit too.
const ROLE_OPTIONS = [
  "Frontend Development", "Backend Development", "Full-stack Development",
  "UI/UX Design", "Hardware / Electronics", "Research & Content",
  "Business & Strategy", "Marketing & Growth", "Pitch & Presentation",
  "Project Management", "Other",
];

const EMPTY = {
  problem_solved: "", prototype_status: "", pitch_video_link: "", repo_link: "", demo_link: "",
  tech_stack: [], changes_from_r1: "", biggest_challenge: "",
  next_step_if_win: "", prize_money_usage: [], prize_money_usage_other: "", build_next: "", team_roles: [],
  public_consent: false,
};

export default function SubmitRound2() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [techCustom, setTechCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/submissions/r2");
        setState(data);
        const members = data.team_members || [];
        const s = data.submission;
        const savedRoles = Array.isArray(s?.team_roles) ? s.team_roles : [];
        const memberRoles = members.map(m => {
          const found = savedRoles.find(r => r.name === m.name);
          const role = found?.role || "";
          const preset = role === "" || ROLE_OPTIONS.includes(role);
          return { name: m.name, role: preset ? role : "Other", other: preset ? "" : role };
        });
        if (s) {
          setForm({
            problem_solved: s.problem_solved || "",
            prototype_status: s.prototype_status || "",
            pitch_video_link: s.pitch_video_link || "",
            repo_link: s.repo_link || "",
            demo_link: s.demo_link || "",
            tech_stack: s.tech_stack || [],
            changes_from_r1: s.changes_from_r1 || "",
            biggest_challenge: s.biggest_challenge || "",
            next_step_if_win: s.next_step_if_win || "",
            prize_money_usage: s.prize_money_usage || [],
            prize_money_usage_other: s.prize_money_usage_other || "",
            build_next: s.build_next || "",
            team_roles: memberRoles,
            public_consent: !!s.public_consent,
          });
        } else {
          setForm(f => ({ ...f, team_roles: memberRoles }));
        }
      } catch (err) { toast.error(errorMessage(err)); }
      setLoading(false);
    })();
  }, []);

  const isSubmitted = state?.submission?.status === "submitted";
  const readOnly = isSubmitted || !state?.eligible;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleTech = (t) => setForm(f => ({ ...f, tech_stack: f.tech_stack.includes(t) ? f.tech_stack.filter(x => x !== t) : [...f.tech_stack, t] }));
  const addCustomTech = () => {
    const v = techCustom.trim();
    if (v && !form.tech_stack.includes(v)) setForm(f => ({ ...f, tech_stack: [...f.tech_stack, v] }));
    setTechCustom("");
  };
  const togglePrize = (v) => setForm(f => ({ ...f, prize_money_usage: f.prize_money_usage.includes(v) ? f.prize_money_usage.filter(x => x !== v) : [...f.prize_money_usage, v] }));
  const setMemberRole = (idx, key, val) => setForm(f => ({ ...f, team_roles: f.team_roles.map((m, i) => i === idx ? { ...m, [key]: val } : m) }));

  const validateClient = () => {
    if (!form.problem_solved.trim()) return "Please describe what problem your prototype solves";
    if (!form.prototype_status) return "Please select your current prototype status";
    if (!/^https?:\/\//.test(form.pitch_video_link)) return "Pitch video link must be a valid URL";
    if (form.repo_link && !/^https?:\/\//.test(form.repo_link)) return "Repository link must be a valid URL or left empty";
    if (form.demo_link && !/^https?:\/\//.test(form.demo_link)) return "Demo link must be a valid URL or left empty";
    if (!form.changes_from_r1.trim()) return "Tell us what changed from Round 1";
    if (!form.biggest_challenge.trim()) return "Tell us the biggest challenge you faced";
    return null;
  };

  const buildPayload = () => ({
    ...form,
    prize_money_usage_other: form.prize_money_usage.includes("Other") ? form.prize_money_usage_other : "",
    team_roles: form.team_roles
      .map(m => ({ name: m.name, role: m.role === "Other" ? (m.other || "").trim() || "Other" : m.role }))
      .filter(m => m.role && m.role.trim()),
  });

  const saveDraft = async () => {
    setBusy(true);
    try {
      await api.post("/submissions/r2/save", buildPayload());
      toast.success("Draft saved");
      const { data } = await api.get("/submissions/r2");
      setState(data);
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const finalSubmit = async () => {
    const err = validateClient();
    if (err) { toast.error(err); setConfirming(false); return; }
    setBusy(true);
    try {
      await api.post("/submissions/r2/submit", buildPayload());
      toast.success("Submitted! Best of luck.");
      navigate("/team/dashboard");
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); setConfirming(false); }
  };

  if (loading) return <div className="App"><Navbar /><div className="min-h-[60vh] flex items-center justify-center"><span className="spinner" /></div></div>;

  // Only the team leader can submit — non-leaders see a clear message (not the editable form).
  if (state?.team && state?.is_leader === false) {
    return (
      <div className="App" data-testid="submit-r2-not-leader">
        <Navbar />
        <div className="max-w-2xl mx-auto px-5 sm:px-8 py-16 text-center">
          <button onClick={() => navigate("/team/dashboard")} className="text-gray-400 hover:text-white text-sm flex items-center gap-2 mb-8 mx-auto"><ArrowLeft className="w-4 h-4" /> Back to dashboard</button>
          <div className="inline-flex items-center gap-2 badge badge-invited mb-6"><Lock className="w-3.5 h-3.5" /> Team leader only</div>
          <h1 className="font-display text-4xl text-white">Only your team leader can submit</h1>
          <p className="text-gray-400 mt-4 leading-relaxed">
            Round 2 for <span className="text-white">{state.team.name}</span> is submitted by your team leader{state.leader_name ? <> (<span className="text-white">{state.leader_name}</span>)</> : ""}. You can track your team's progress from the dashboard.
          </p>
          {isSubmitted && <p className="text-emerald-400 mt-4" data-testid="r2-already-submitted-note">Good news — your team has already submitted Round 2.</p>}
          <button onClick={() => navigate("/team/dashboard")} className="btn-primary mt-8" data-testid="r2-not-leader-dashboard-btn">Go to dashboard</button>
        </div>
        <Footer />
      </div>
    );
  }

  if (!state?.eligible && !isSubmitted) {
    return (
      <div className="App" data-testid="submit-r2-not-eligible">
        <Navbar />
        <div className="max-w-2xl mx-auto px-5 sm:px-8 py-16">
          <button onClick={() => navigate("/team/dashboard")} className="text-gray-400 hover:text-white text-sm flex items-center gap-2 mb-6"><ArrowLeft className="w-4 h-4" /> Back to dashboard</button>
          <h1 className="font-display text-4xl text-white">Round 2 submission is <span className="text-brand">not available</span></h1>
          <p className="text-gray-400 mt-4">{state?.reason || "Only shortlisted teams can submit Round 2."}</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App" data-testid="submit-r2-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
        <button onClick={() => navigate("/team/dashboard")} className="text-gray-400 hover:text-white text-sm flex items-center gap-2 mb-6"><ArrowLeft className="w-4 h-4" /> Back to dashboard</button>
        <div className="label-eyebrow mb-2">Round 2 · Build</div>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="font-display text-4xl text-white">Ship your prototype</h1>
          {isSubmitted && <span className="badge badge-accepted" data-testid="submitted-badge">submitted</span>}
        </div>
        {isSubmitted && <p className="text-sm text-gray-400 mt-3">This submission is final and cannot be edited.</p>}

        <div className="mt-10 space-y-6">
          {/* 1. Problem solved */}
          <Field label="What problem does your prototype solve?" testid="r2-problem"
                 hint="Keep it concise — 2 to 3 sentences max" counter={`${form.problem_solved.length}/300`}>
            <textarea disabled={readOnly} value={form.problem_solved} onChange={e => set("problem_solved", e.target.value.slice(0, 300))}
              rows={3} maxLength={300} className="input-field resize-none"
              placeholder="Brief summary of the problem you're solving and who it affects" data-testid="r2-problem-input" />
          </Field>

          {/* 2. Prototype status */}
          <Field label="Current prototype status" testid="r2-status"
                 hint="Be honest — judges will verify against your demo and repo links">
            <select disabled={readOnly} value={form.prototype_status} onChange={e => set("prototype_status", e.target.value)}
              className="input-field" data-testid="r2-status-select">
              <option value="">Select status…</option>
              {PROTOTYPE_STATUS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>

          {/* 3. Pitch video */}
          <Field label="Pitch Video Link" testid="r2-video" hint="Record a 5-minute pitch video. Any link accepted — YouTube, Loom, Google Drive.">
            <input disabled={readOnly} value={form.pitch_video_link} onChange={e => set("pitch_video_link", e.target.value)} className="input-field" placeholder="https://…" data-testid="r2-video-input" />
          </Field>

          {/* 4. Source repo (optional) */}
          <Field label="Source Repository Link" testid="r2-repo" optional hint="GitHub, GitLab, or any public repo. Leave empty if your build is hardware or has no public repo.">
            <input disabled={readOnly} value={form.repo_link} onChange={e => set("repo_link", e.target.value)} className="input-field" placeholder="https://github.com/…" data-testid="r2-repo-input" />
          </Field>

          {/* 5. Live demo (optional) */}
          <Field label="Live Demo Link" testid="r2-demo" optional hint="Deployed demo, Replit, CodeSandbox, or any accessible link.">
            <input disabled={readOnly} value={form.demo_link} onChange={e => set("demo_link", e.target.value)} className="input-field" placeholder="https://…" data-testid="r2-demo-input" />
          </Field>

          {/* 6. Tech stack (optional) */}
          <Field label="Tech Stack Used" testid="r2-tech" optional hint="Select what your team used to build this prototype. Skip if hardware-based or not applicable.">
            <div className="flex flex-wrap gap-2 mb-3">
              {TECH_OPTIONS.map(t => (
                <button type="button" key={t} disabled={readOnly} onClick={() => toggleTech(t)}
                  className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest border ${form.tech_stack.includes(t) ? "bg-brand text-black border-brand" : "border-white/15 text-gray-400 hover:text-white"}`}
                  data-testid={`r2-tech-${t}`}>{t}</button>
              ))}
              {form.tech_stack.filter(t => !TECH_OPTIONS.includes(t)).map(t => (
                <span key={t} className="px-3 py-1.5 text-xs font-mono uppercase tracking-widest bg-brand/20 text-brand border border-brand/40 flex items-center gap-1">
                  {t}{!readOnly && <button onClick={() => toggleTech(t)}><X className="w-3 h-3" /></button>}
                </span>
              ))}
            </div>
            {!readOnly && (
              <div className="flex gap-2">
                <input type="text" value={techCustom} onChange={e => setTechCustom(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addCustomTech())}
                  placeholder="Add custom tech…" className="input-field flex-1" data-testid="r2-tech-custom" />
                <button type="button" onClick={addCustomTech} className="btn-secondary !py-2 !px-3"><Plus className="w-4 h-4" /></button>
              </div>
            )}
          </Field>

          {/* 7. Changes from R1 */}
          <Field label="What Changed from Round 1?" testid="r2-changes" counter={`${form.changes_from_r1.length}/400`}>
            <textarea disabled={readOnly} value={form.changes_from_r1} onChange={e => set("changes_from_r1", e.target.value.slice(0, 400))}
              rows={4} maxLength={400} className="input-field resize-none" data-testid="r2-changes-input" />
          </Field>

          {/* 8. Biggest challenge */}
          <Field label="Biggest Challenge Faced" testid="r2-challenge"
                 hint="What was the hardest technical or product challenge you faced while building this prototype?"
                 counter={`${form.biggest_challenge.length}/400`}>
            <textarea disabled={readOnly} value={form.biggest_challenge} onChange={e => set("biggest_challenge", e.target.value.slice(0, 400))}
              rows={4} maxLength={400} className="input-field resize-none" data-testid="r2-challenge-input" />
          </Field>

          {/* 9. Next step if win (optional) */}
          <Field label="What's your next step if you win?" testid="r2-next-step" optional
                 hint="Tell us how winning will help you move this idea forward" counter={`${(form.next_step_if_win || "").length}/300`}>
            <textarea disabled={readOnly} value={form.next_step_if_win} onChange={e => set("next_step_if_win", e.target.value.slice(0, 300))}
              rows={3} maxLength={300} className="input-field resize-none"
              placeholder="Ex: Launch a beta, file a patent, attend an accelerator, hire a developer" data-testid="r2-next-step-input" />
          </Field>

          {/* 10. Prize money usage (optional multi-select) */}
          <Field label="How would you primarily use the prize money?" testid="r2-prize-usage" optional
                 hint="Select all that apply — this helps us connect winners with the right resources and credits">
            <div className="grid sm:grid-cols-2 gap-2.5">
              {PRIZE_USAGE.map(o => {
                const active = form.prize_money_usage.includes(o.value);
                return (
                  <button type="button" key={o.value} disabled={readOnly} onClick={() => togglePrize(o.value)}
                    className={`flex items-start gap-2.5 text-left px-3 py-2.5 border text-sm transition-colors ${active ? "bg-brand/15 border-brand/50 text-white" : "border-white/10 text-gray-400 hover:text-white hover:border-white/25"}`}
                    data-testid={`r2-prize-${o.value}`}>
                    <span className={`mt-0.5 w-4 h-4 flex-shrink-0 border flex items-center justify-center ${active ? "bg-brand border-brand" : "border-white/30"}`}>
                      {active && <CheckCircle2 className="w-3 h-3 text-black" />}
                    </span>
                    <span>{o.label}</span>
                  </button>
                );
              })}            </div>
            {form.prize_money_usage.includes("Other") && (
              <input disabled={readOnly} value={form.prize_money_usage_other} onChange={e => set("prize_money_usage_other", e.target.value.slice(0, 100))}
                maxLength={100} className="input-field mt-3" placeholder="Tell us how (max 100 chars)…" data-testid="r2-prize-other-input" />
            )}
          </Field>

          {/* 11. Build next (optional) */}
          <Field label="What would you build next?" testid="r2-build-next" optional
                 hint="Where does this product go after the hackathon?" counter={`${(form.build_next || "").length}/300`}>
            <textarea disabled={readOnly} value={form.build_next} onChange={e => set("build_next", e.target.value.slice(0, 300))}
              rows={3} maxLength={300} className="input-field resize-none"
              placeholder="Ex: Add payment integration, expand to 3 more cities, build mobile app" data-testid="r2-build-next-input" />
          </Field>

          {/* 12. Team roles — per member (optional) */}
          <Field label="Team roles — who built what?" testid="r2-roles" optional
                 hint="Pick a role for each teammate so judges understand how your team worked together.">
            {form.team_roles.length === 0 ? (
              <div className="text-sm text-gray-500 italic">Team members will appear here once added to your team.</div>
            ) : (
              <div className="space-y-3" data-testid="r2-roles-list">
                {form.team_roles.map((m, idx) => (
                  <div key={idx} className="flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`r2-role-row-${idx}`}>
                    <div className="sm:w-44 flex-shrink-0 text-sm text-white font-medium truncate">{m.name}</div>
                    <select disabled={readOnly} value={m.role} onChange={e => setMemberRole(idx, "role", e.target.value)}
                      className="input-field flex-1" data-testid={`r2-role-select-${idx}`}>
                      <option value="">Select role…</option>
                      {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                    {m.role === "Other" && (
                      <input disabled={readOnly} value={m.other} onChange={e => setMemberRole(idx, "other", e.target.value.slice(0, 60))}
                        maxLength={60} className="input-field flex-1" placeholder="Specify role…" data-testid={`r2-role-other-${idx}`} />
                    )}
                  </div>
                ))}
              </div>
            )}
          </Field>

          {/* 13. Public visibility consent */}
          <div className="pt-2" data-testid="r2-consent">
            <button
              type="button"
              disabled={readOnly}
              onClick={() => set("public_consent", !form.public_consent)}
              className={`flex items-start gap-3 w-full text-left p-4 border transition-colors ${form.public_consent ? "border-brand/50 bg-brand/10" : "border-white/10 hover:border-white/25"} ${readOnly ? "opacity-70 cursor-default" : ""}`}
              data-testid="r2-consent-toggle"
            >
              <span className={`mt-0.5 w-5 h-5 flex-shrink-0 border flex items-center justify-center ${form.public_consent ? "bg-brand border-brand" : "border-white/30"}`}>
                {form.public_consent && <CheckCircle2 className="w-3.5 h-3.5 text-black" />}
              </span>
              <span>
                <span className="text-sm text-white font-medium">Make my project publicly visible on the results page</span>
                <span className="block text-xs text-gray-500 mt-1 leading-relaxed">
                  If enabled, your project details will be shown publicly when results are announced. Your team name and ranking will always be shown regardless.
                </span>
              </span>
            </button>
          </div>

          {!readOnly && (
            <div className="flex flex-wrap gap-3 pt-4 border-t border-white/5">
              <button onClick={saveDraft} disabled={busy} className="btn-secondary flex items-center gap-2" data-testid="r2-save-draft">
                {busy ? <span className="spinner" /> : <Save className="w-4 h-4" />} Save Draft
              </button>
              <button onClick={() => setConfirming(true)} disabled={busy} className="btn-primary flex items-center gap-2" data-testid="r2-submit-btn">
                <Send className="w-4 h-4" /> Submit Prototype
              </button>
            </div>
          )}
        </div>
      </div>

      {confirming && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-5" data-testid="confirm-modal">
          <div className="card-surface max-w-md w-full">
            <h2 className="font-display text-2xl text-white">Submit final?</h2>
            <p className="text-gray-400 mt-3">Once submitted you cannot edit your submission. Are you sure?</p>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setConfirming(false)} className="btn-secondary flex-1" data-testid="cancel-submit">Cancel</button>
              <button onClick={finalSubmit} disabled={busy} className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid="confirm-submit">
                {busy ? <span className="spinner" /> : <CheckCircle2 className="w-4 h-4" />} Yes, submit
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

const Field = ({ label, children, testid, hint, optional, counter }) => (
  <div data-testid={testid}>
    <div className="flex items-center justify-between mb-2 gap-3">
      <label className="label-eyebrow flex items-center gap-2">
        {label}
        {optional && <span className="px-2 py-0.5 text-[10px] uppercase tracking-widest bg-white/5 border border-white/10 text-gray-500 rounded-full normal-case">Optional</span>}
      </label>
      {counter && <span className="text-[11px] font-mono text-gray-500">{counter}</span>}
    </div>
    {hint && <div className="text-xs text-gray-500 mb-2">{hint}</div>}
    {children}
  </div>
);
